import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import type { EmployeeIdentity } from '../../services/employees/employees.service';
import { EMPLOYEE_SESSION_EXPIRED_EVENT } from '../../services/index/index.service';

type EmployeeSessionState = {
  employee: EmployeeIdentity;
  employeeSession: string;
  activeSessionId: string;
};

type EmployeeSessionContextType = {
  employeeState: EmployeeSessionState | null;
  setEmployeeState: (tenantId: string, value: EmployeeSessionState) => void;
  clearEmployeeState: (tenantId: string) => void;
  hydrateEmployeeState: (tenantId: string) => EmployeeSessionState | null;
};

const EmployeeSessionContext = createContext<EmployeeSessionContextType | undefined>(undefined);

const buildStorageKey = (tenantId: string) => `posEmployeeSession:${tenantId}`;

const isSameEmployeeSessionState = (
  left: EmployeeSessionState | null,
  right: EmployeeSessionState,
): boolean => {
  if (!left) {
    return false;
  }

  return (
    left.employeeSession === right.employeeSession &&
    left.employee.id === right.employee.id &&
    left.employee.tenantId === right.employee.tenantId &&
    left.employee.role === right.employee.role &&
    left.employee.fullName === right.employee.fullName &&
    left.employee.isActive === right.employee.isActive
  );
};

export const EmployeeSessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [employeeStateByTenant, setEmployeeStateByTenant] = useState<Record<string, EmployeeSessionState>>({});
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);

  // Summary: Clears all tenant-scoped POS employee sessions when backend marks any employee session as expired, ensuring UI reliably returns...
  // Details: docs/function-library/web/contexts-pages.md#contexts-employeesessionprovider
  const clearAllEmployeeSessions = useCallback(() => {
    setEmployeeStateByTenant({});
    setActiveTenantId(null);

    const employeeSessionStorageKeys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith('posEmployeeSession:')) {
        employeeSessionStorageKeys.push(key);
      }
    }

    employeeSessionStorageKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
  }, []);

  // Summary: Hydrates employee POS session from tenant-scoped local storage key so POS shell can survive refresh without forcing immediate r...
  // Details: docs/function-library/web/contexts-pages.md#contexts-employeesessionprovider
  const hydrateEmployeeState = useCallback((tenantId: string): EmployeeSessionState | null => {
    setActiveTenantId(tenantId);
    const raw = localStorage.getItem(buildStorageKey(tenantId));
    if (!raw) {
      setEmployeeStateByTenant((previous) => {
        if (!previous[tenantId]) {
          return previous;
        }

        const next = { ...previous };
        delete next[tenantId];
        return next;
      });
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as EmployeeSessionState;
      setEmployeeStateByTenant((previous) => {
        const current = previous[tenantId] ?? null;
        if (isSameEmployeeSessionState(current, parsed)) {
          return previous;
        }

        return {
          ...previous,
          [tenantId]: parsed,
        };
      });
      return parsed;
    } catch {
      localStorage.removeItem(buildStorageKey(tenantId));
      setEmployeeStateByTenant((previous) => {
        if (!previous[tenantId]) {
          return previous;
        }

        const next = { ...previous };
        delete next[tenantId];
        return next;
      });
      return null;
    }
  }, []);

  // Summary: Persists tenant-scoped employee POS session after successful PIN identification for subsequent manager/employee tab authorization.
  // Details: docs/function-library/web/contexts-pages.md#contexts-employeesessionprovider
  const setEmployeeState = useCallback((tenantId: string, value: EmployeeSessionState) => {
    setActiveTenantId(tenantId);
    localStorage.setItem(buildStorageKey(tenantId), JSON.stringify(value));
    setEmployeeStateByTenant((previous) => {
      const current = previous[tenantId] ?? null;
      if (isSameEmployeeSessionState(current, value)) {
        return previous;
      }

      return {
        ...previous,
        [tenantId]: value,
      };
    });
  }, []);

  // Summary: Clears tenant-scoped employee POS session during logout or identity switch flows to prevent stale employee authorization contex...
  // Details: docs/function-library/web/contexts-pages.md#contexts-employeesessionprovider
  const clearEmployeeState = useCallback((tenantId: string) => {
    localStorage.removeItem(buildStorageKey(tenantId));
    setEmployeeStateByTenant((previous) => {
      if (!previous[tenantId]) {
        return previous;
      }

      const next = { ...previous };
      delete next[tenantId];
      return next;
    });
    setActiveTenantId((previous) => (previous === tenantId ? null : previous));
  }, []);

  // Summary: Subscribes to global transport-layer employee session expiration signals and resets provider state so all POS surfaces converge...
  // Details: docs/function-library/web/contexts-pages.md#contexts-employeesessionprovider
  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleSessionExpired = () => {
      clearAllEmployeeSessions();
    };

    window.addEventListener(EMPLOYEE_SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => {
      window.removeEventListener(EMPLOYEE_SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [clearAllEmployeeSessions]);

  const employeeState = activeTenantId ? employeeStateByTenant[activeTenantId] ?? null : null;

  const contextValue = useMemo(
    () => ({
      employeeState,
      setEmployeeState,
      clearEmployeeState,
      hydrateEmployeeState,
    }),
    [clearEmployeeState, employeeState, hydrateEmployeeState, setEmployeeState],
  );

  return (
    <EmployeeSessionContext.Provider value={contextValue}>
      {children}
    </EmployeeSessionContext.Provider>
  );
};

export const useEmployeeSession = () => {
  const context = useContext(EmployeeSessionContext);
  if (!context) {
    throw new Error('useEmployeeSession must be used within EmployeeSessionProvider');
  }

  return context;
};

